import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { PortalDsField, PortalDsSelect } from 'src/components/portal-design-system/portal-ds-field';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { snapToThirtyMinuteSlot } from 'src/helpers/scheduler-time.helpers';
import { SchedulerTimePicker } from 'src/pages/operations/components/scheduler-time-picker';
import {
  BOOKING_TIMEZONE_OPTIONS,
  DEFAULT_BOOKING_END_TIME,
  DEFAULT_BOOKING_START_TIME,
  DEFAULT_BOOKING_TIMEZONE,
  formatEffectiveBookingWindow,
} from '../constants/booking-window.constants';
import { useSchedulingSettings } from '../hooks/use-scheduling-settings';
import type { SchedulingSettingsFieldErrors } from '../types/scheduling.types';

interface BookingWindowSettingsProps {
  disabled?: boolean;
}

function apiTimeToPickerValue(value: string | undefined, fallback: string): string {
  if (!value) {
    return fallback;
  }
  return snapToThirtyMinuteSlot(value.slice(0, 5));
}

export function BookingWindowSettings({ disabled = false }: BookingWindowSettingsProps) {
  const { settings, isLoading, isError, isSaving, saveBookingWindow } = useSchedulingSettings();
  const [startTime, setStartTime] = useState(DEFAULT_BOOKING_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_BOOKING_END_TIME);
  const [timezone, setTimezone] = useState(DEFAULT_BOOKING_TIMEZONE);
  const [fieldErrors, setFieldErrors] = useState<SchedulingSettingsFieldErrors>({});

  useEffect(() => {
    if (settings) {
      setStartTime(apiTimeToPickerValue(settings.booking_start_time, DEFAULT_BOOKING_START_TIME));
      setEndTime(apiTimeToPickerValue(settings.booking_end_time, DEFAULT_BOOKING_END_TIME));
      setTimezone(settings.booking_timezone || DEFAULT_BOOKING_TIMEZONE);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-[#5E6278]">
        <Loader2
          className="size-4 animate-spin"
          aria-hidden
        />
        <span className={portalDs.caption}>
          Loading booking window…
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn(portalDs.caption, 'mb-6 text-[#DC2626]')}>
        Could not load the booking window settings.
      </p>
    );
  }

  const handleSave = async() => {
    setFieldErrors({});

    if (startTime >= endTime) {
      setFieldErrors({
        booking_end_time: 'Finish time must be after start time.',
      });
      return;
    }

    const result = await saveBookingWindow(startTime, endTime, timezone);
    if (!result.ok) {
      if (result.fieldErrors) {
        setFieldErrors(result.fieldErrors);
        return;
      }

      setFieldErrors({
        booking_end_time: result.message ?? 'Failed to save booking window.',
      });
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-[#EDEEF2] bg-white p-4 shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
      <p className="text-[13px] font-semibold text-[#0D0F1A]">
        Booking window
      </p>
      <p className={cn(portalDs.caption, 'mt-1')}>
        Set the hours when Studio and Flow visitors can book meetings.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <PortalDsField
          label="Start time"
          htmlFor="booking-window-start"
          error={fieldErrors.booking_start_time}
        >
          <SchedulerTimePicker
            id="booking-window-start"
            value={startTime}
            onChange={setStartTime}
            disabled={disabled || isSaving}
          />
        </PortalDsField>

        <PortalDsField
          label="Finish time"
          htmlFor="booking-window-end"
          error={fieldErrors.booking_end_time}
        >
          <SchedulerTimePicker
            id="booking-window-end"
            value={endTime}
            onChange={setEndTime}
            disabled={disabled || isSaving}
          />
        </PortalDsField>

        <PortalDsField
          label="Timezone"
          htmlFor="booking-window-timezone"
          error={fieldErrors.booking_timezone}
        >
          <PortalDsSelect
            id="booking-window-timezone"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            disabled={disabled || isSaving}
          >
            {BOOKING_TIMEZONE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </PortalDsSelect>
        </PortalDsField>
      </div>

      <p className={cn(portalDs.caption, 'mt-3')}>
        {formatEffectiveBookingWindow(startTime, endTime, timezone)}
      </p>

      <div className="mt-4">
        <PortalDsButton
          type="button"
          variant="primary"
          size="sm"
          disabled={disabled || isSaving}
          onClick={() => void handleSave()}
        >
          {isSaving ? 'Saving…' : 'Save booking window'}
        </PortalDsButton>
      </div>
    </div>
  );
}
